import { FC, FormEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import '@ant-design/compatible/assets/index.css'
import { Form, Input } from 'antd'
import IncorrectInputAlert from '@User/IncorrectInputAlert'
import {
    VERIFICATION_CODE,
    ENABLE_MFA_RULE,
    VERIFICATION_CODE_LENGTH,
} from '@User/MyAccountPage/AccountSettings/constants'
import styles from '@User/styles.css'

import Box from '~/core/Box'
import Button from '~/core/Button'
import Text from '~/core/Text'
import { onlyNumsWithLength } from '~/lib/normalize'
import getColor from '~/lib/getColor'
import { handleMfaSubmit } from '../logic'
import { areAllRequiredFieldsFilled } from '~/lib/formHelpers'

const PLACEMENT = 'mfa-form'

type MfaSectionProps = {
    session: string
    email: string
    returnTo?: string | null
    queryString?: string
}

const MfaSection: FC<MfaSectionProps> = ({ email, session }) => {
    const client = useApolloClient()
    const dispatch = useDispatch()
    const [loading, setLoading] = useState<boolean>(false)
    const [hasError, setHasError] = useState<boolean>(false)
    const [formFilled, setFormFilled] = useState(false)
    const [entryError, setEntryError] = useState(false)

    const [form] = Form.useForm()
    const { validateFields } = form

    const checkForErrors = async () => {
        try {
            await validateFields([VERIFICATION_CODE])
            setEntryError(false)
        } catch (err: any) {
            /*
             validateFields is async and a little wonky. This would normally
             always be an error, but we can get no errors with outOfDate: true
             when a password manager fills the form, so this solves for that
             case.
             */

            if (err?.errorFields?.length) {
                setEntryError(true)
            } else {
                setEntryError(false)
            }
        }
    }

    const handleFormSubmit = (evt: Event | FormEvent) => {
        evt?.preventDefault?.()
        handleMfaSubmit({
            form,
            email,
            session,
            setLoading,
            setHasError,
            options: { dispatch, client },
        })
    }

    const handleChangedValues = () => {
        setFormFilled(areAllRequiredFieldsFilled(form, [VERIFICATION_CODE]))
    }

    return (
        <Box className={styles.section}>
            <Box className={styles.header}>Two-factor authentication</Box>
            <Box alignItems="center" direction="col" width="100%">
                {hasError && (
                    <IncorrectInputAlert type="mfa" placement={PLACEMENT} />
                )}
                <Text style={{ marginBottom: '40px' }}>
                    Please enter the code from your authenticator app below
                </Text>
                <Form
                    onFinish={handleFormSubmit}
                    style={{ width: '100%' }}
                    form={form}
                    onValuesChange={handleChangedValues}
                >
                    <Text
                        fontSize="14px"
                        color="blackAlpha65"
                        style={{ marginBottom: 0 }}
                        fontWeight={400}
                    >
                        Verification code
                    </Text>
                    <Form.Item
                        name={VERIFICATION_CODE}
                        validateTrigger="onBlur"
                        rules={ENABLE_MFA_RULE}
                        normalize={value =>
                            onlyNumsWithLength(value, VERIFICATION_CODE_LENGTH)
                        }
                    >
                        <Input
                            data-test-target={VERIFICATION_CODE}
                            placeholder="Enter the verification code here"
                            autoComplete="off"
                            autoFocus
                            onBlur={checkForErrors}
                            onChange={() => setHasError(false)}
                            style={{
                                borderColor:
                                    hasError && getColor('errorAlpha85'),
                            }}
                        />
                    </Form.Item>
                    <Form.Item name={`${PLACEMENT}-submit`}>
                        <Box justifyContent="center">
                            <Button
                                disabled={
                                    hasError ||
                                    loading ||
                                    !formFilled ||
                                    entryError
                                }
                                data-test-target={`${PLACEMENT}-submit`}
                                loading={loading}
                                type="primary"
                                size="large"
                                name="log-in"
                                placement={PLACEMENT}
                                style={{ width: '167px' }}
                                htmlType="submit"
                                onClick={handleFormSubmit}
                            >
                                {loading ? 'Logging in' : 'Log in'}
                            </Button>
                        </Box>
                    </Form.Item>
                </Form>
            </Box>
        </Box>
    )
}

export default MfaSection
